/**
 * Public Report Verification Page
 * Route: /verify/report/:reportId
 * No authentication required — accessible via QR code scan.
 *
 * Terminology aligned with platform semantics:
 * - "Climate & Circularity Report" (not "Credentials Report")
 * - 3-layer methodology (Calculation Basis / Protocol / Crediting Pathway)
 * - "Validation-Ready Model Output" status (not "VERIFIED")
 * - Verification status derived from verification_level, never implied from platform registration
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, AlertCircle, RefreshCw, ExternalLink, Shield, Info } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

// ── Report type → display name mapping ──────────────────────────────────────
// Platform-wide naming aligned with latest report naming conventions.
const REPORT_TYPE_LABELS = {
  "credentials-report":      "Climate & Circularity Report",
  "biogas-finance-brief":    "Finance Brief",
  "emissions-report":        "Emissions Calculation Report",
  "mrv-plan":                "Monitoring, Reporting & Verification Plan",
  "pdd":                     "Project Design Document (Draft)",
  "finance-brief":           "Finance Brief",
  "credit-committee":        "Credit Committee Pack",
  // Dangote corporate reports
  "dangote-corporate":       "Climate & Circularity Report",
  "dangote-value-strategy":  "Carbon Value & Strategy Report",
  "dangote-financing":       "Financing Readiness Report",
  "dangote-sdg":             "SDG Contribution Report",
  "dangote-loa":             "Article 6 LoA Pack (Draft)",
};
function reportTypeLabel(rawType) {
  if (!rawType) return "Corporate Report";
  return REPORT_TYPE_LABELS[rawType]
    || rawType.replace(/dangote-/g, "").replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase());
}

// Detect Dangote corporate report by type prefix
function isDangoteReport(rawType) {
  return rawType && rawType.startsWith("dangote-");
}

// Extract period label from project_name ("Dangote Industries — Jan 2026" → "Jan 2026")
function extractPeriodLabel(projectName) {
  const match = (projectName || "").match(/\u2014\s*(.+)$/);
  return match ? match[1].trim() : "—";
}

// Extract report ID from project_name + report_type
function extractReportId(reportType, periodLabel) {
  const typeMap = {
    "dangote-corporate":      "DCI-CORP",
    "dangote-value-strategy": "DCI-CVS",
    "dangote-financing":      "DCI-FIN",
    "dangote-sdg":            "DCI-SDG",
    "dangote-loa":            "DCI-LOA",
  };
  const prefix = typeMap[reportType] || "DCI";
  const period = periodLabel.replace(" ", "-").toUpperCase();
  return `${prefix}-${period}`;
}

// ── Verification level → display status ─────────────────────────────────────
// Conservative and consistent with workspace badge approach:
// PRIMARY status is ALWAYS "Validation-Ready Model Output" regardless of platform verification.
// Verification status is shown as a SEPARATE secondary field.
// This prevents the QR page from making a stronger claim than the platform supports.
// "Independently Verified" in the SECONDARY status only when VVB actually completed workflow.
function verificationStatusLabel(level) {
  if (level === "verified_independent") return "Independently Verified by External Body";
  if (level === "reviewed_internal")    return "Verification: Not Initiated";
  return "Verification: Not Initiated";
}

// ── Methodology 3-layer derivation ────────────────────────────────────────
// The `methodology` field in PublicReport stores the protocol ID (e.g. "DCI-BIOGAS-V1.0").
// We derive the full 3-layer architecture for display.
function deriveMethodologyLayers(methodologyField) {
  const protocol   = methodologyField || "DCI-BIOGAS-V1.0";
  const calcBasis  = "IPCC 2006 Guidelines";
  const creditPath = "Not Assigned";
  return { calcBasis, protocol, creditPath };
}

export default function VerifyReport() {
  const { reportId } = useParams();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!reportId) { setError("No report ID provided."); setLoading(false); return; }
    // Detect FI institutional report (64-char hex SHA-256 hash)
    const isFIHash = /^[A-F0-9]{64}$/i.test(reportId);
    if (isFIHash) {
      // Try workspace report first (project workspace PDFs), then FI
      const wsUrl = `${process.env.REACT_APP_BACKEND_URL}/api/workspace/verify/${reportId}`;
      fetch(wsUrl)
        .then(r => r.json())
        .then(d => {
          if (d.verified) {
            setData({ _type: "workspace", ...d });
            setLoading(false);
          } else {
            throw new Error("not_workspace");
          }
        })
        .catch(() => {
          // Try FI public verification
          const fiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/fi/verify/${reportId}`;
          fetch(fiUrl)
            .then(r => r.json())
            .then(d => {
              if (d.detail) throw new Error("Report not found");
              setData({ _type: "fi", ...d });
              setLoading(false);
            })
            .catch(() => { setError("Report not found or not publicly verifiable."); setLoading(false); });
        });
      return;
    }
    // Legacy report ID formats
    const isHousing = reportId.startsWith("CCR-HOUSE-");
    const path = isHousing
      ? `/api/public/housing/verify/${reportId}`
      : `/api/public/sme/verify/${reportId}`;
    const url = `${process.env.REACT_APP_BACKEND_URL}${path}`;
    fetch(url)
      .then(r => { if (!r.ok) throw new Error("Report not found"); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [reportId]);

  if (loading) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
      <RefreshCw size={24} className="text-teal-400 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="DeCarbonIQ" className="h-8 mx-auto mb-2" style={{ filter: "brightness(0) invert(1)" }} />
          <p className="text-[10px] text-slate-500 uppercase tracking-widest">Climate Intelligence Platform</p>
        </div>

        {error ? (
          <div className="bg-slate-800 border border-red-500/30 rounded-xl p-6 text-center">
            <AlertCircle size={32} className="text-red-400 mx-auto mb-3" />
            <p className="text-base font-bold text-white mb-1">Report Not Found</p>
            <p className="text-sm text-slate-400">
              This report could not be verified. It may not be registered in the public verification system.
            </p>
          </div>

        ) : data?._type === "workspace" ? (
          /* ── Workspace Project Report Verification ────────────────────── */
          (() => {
            const { calcBasis, protocol, creditPath } = deriveMethodologyLayers(data.methodology);
            const verLabel    = verificationStatusLabel(data.verification_level);
            const reportLabel = reportTypeLabel(data.report_type);
            // QR public page rule: ALWAYS show "Verification: Not Initiated" as secondary status.
            // This page must never imply independent verification — detailed VVB status
            // is available via the project workspace for authorized users only.
            const verNotInitiated = true; // always show "Not Initiated" on QR page
            return (
              <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden" data-testid="workspace-verify-card">
                {/* Status header — ALWAYS "Validation-Ready Model Output" as primary */}
                <div className={`border-b px-6 py-4 flex items-center gap-3 ${isDangoteReport(data.report_type) ? "bg-emerald-500/10 border-emerald-500/20" : "bg-teal-500/10 border-teal-500/20"}`}>
                  <Shield size={20} className="text-teal-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-bold text-teal-400" data-testid="verify-status-label">
                      {isDangoteReport(data.report_type) ? "Report Authenticated" : "Validation-Ready Model Output"}
                    </p>
                    <p className="text-[10px] text-slate-500">
                      Platform-generated · {reportTypeLabel(data.report_type)}
                    </p>
                  </div>
                </div>

                <div className="p-6 space-y-4">
                  {/* Company / Project */}
                  <div>
                    <p className="text-[9px] text-slate-500 uppercase tracking-wide mb-0.5">
                      {isDangoteReport(data.report_type) ? "Company" : "Project"}
                    </p>
                    <p className="text-lg font-bold text-white">{data.project_name?.split(" —")[0] || data.project_name}</p>
                    <p className="text-xs text-slate-400">{data.org_name} · {data.sector} · {data.country}</p>
                  </div>

                  {/* Report info grid — enhanced for Dangote */}
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    {[
                      ["Report ID",   isDangoteReport(data.report_type)
                        ? extractReportId(data.report_type, extractPeriodLabel(data.project_name))
                        : (data.report_hash?.slice(0,16) + "...")],
                      ["Report Type", reportTypeLabel(data.report_type)],
                      ...(isDangoteReport(data.report_type) ? [
                        ["Period",    extractPeriodLabel(data.project_name)],
                        ["Generated", data.generated_at
                          ? new Date(data.generated_at).toLocaleString("en-GB", {day:"2-digit",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})
                          : "—"],
                      ] : [
                        ["Generated", data.generated_at ? new Date(data.generated_at).toLocaleDateString() : "—"],
                      ]),
                    ].map(([l, v]) => (
                      <div key={l} className="bg-slate-700/50 rounded-lg p-2.5">
                        <p className="text-[9px] text-slate-400 uppercase tracking-wide mb-0.5">{l}</p>
                        <p className="text-xs font-bold text-white truncate">{v || "—"}</p>
                      </div>
                    ))}
                  </div>

                  {/* Dangote-specific origin note */}
                  {isDangoteReport(data.report_type) && (
                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3 flex items-start gap-2"
                      data-testid="origin-note">
                      <Shield size={12} className="text-amber-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-[9px] font-bold text-amber-300 uppercase tracking-wide mb-0.5">
                          Verification Scope
                        </p>
                        <p className="text-[10px] text-amber-200/80">
                          Verification confirms report origin only. Not independently verified.
                        </p>
                        <p className="text-[9px] text-slate-500 mt-0.5">
                          This record confirms this report was generated by DeCarbonIQ from the approved Dangote Industries monthly dataset.
                          It does not constitute independent GHG verification, audit, or regulatory approval.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* 3-layer methodology — replaces single ambiguous "Methodology" field */}
                  <div className="bg-slate-700/30 rounded-xl p-3 space-y-2" data-testid="methodology-layers">
                    <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold">Methodology Architecture</p>
                    {[
                      ["Calculation Basis",  calcBasis,  "text-slate-300"],
                      ["Protocol",           protocol,   "text-teal-300 font-mono"],
                      ["Crediting Pathway",  creditPath, creditPath === "Not Assigned" ? "text-amber-400" : "text-emerald-400"],
                    ].map(([l, v, c]) => (
                      <div key={l} className="flex items-start justify-between gap-2">
                        <span className="text-[9px] text-slate-500 flex-shrink-0">{l}</span>
                        <span className={`text-[10px] font-semibold text-right ${c}`}>{v}</span>
                      </div>
                    ))}
                  </div>

                  {/* ── Calculation values (Run 58 approved baseline) ─────── */}
                  {(data.annual_reduction_tco2e || data.scope1_reduction_tco2e) && (
                    <div className="bg-slate-700/30 rounded-xl p-3 space-y-2" data-testid="calc-values">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-[9px] text-slate-400 uppercase tracking-wide font-semibold">Approved GHG Reduction</p>
                        {data.approved_run_id && (
                          <span className="text-[8px] font-mono bg-emerald-900/50 text-emerald-400 px-1.5 py-0.5 rounded">
                            Run {data.approved_run_id} · Approved Baseline
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {[
                          ["Annual Reduction",  data.annual_reduction_tco2e  ? `${Number(data.annual_reduction_tco2e).toLocaleString("en-US",{maximumFractionDigits:1})} tCO₂e/yr` : "—", "text-emerald-400"],
                          ["Scope 1 Reduction", data.scope1_reduction_tco2e  ? `${Number(data.scope1_reduction_tco2e).toLocaleString("en-US",{maximumFractionDigits:1})} tCO₂e/yr` : "—", "text-slate-300"],
                          ["Scope 2 Reduction", data.scope2_reduction_tco2e  ? `${Number(data.scope2_reduction_tco2e).toLocaleString("en-US",{maximumFractionDigits:1})} tCO₂e/yr` : "—", "text-slate-300"],
                          ["Lifetime Reduction",data.lifetime_reduction_tco2e ? `${Number(data.lifetime_reduction_tco2e).toLocaleString("en-US",{maximumFractionDigits:0})} tCO₂e` : "—", "text-slate-300"],
                        ].map(([l, v, c]) => (
                          <div key={l} className="bg-slate-800/60 rounded-lg p-2">
                            <p className="text-[8px] text-slate-500 uppercase tracking-wide mb-0.5">{l}</p>
                            <p className={`text-xs font-bold ${c}`}>{v}</p>
                          </div>
                        ))}
                      </div>
                      {data.data_confidence_score > 0 && (
                        <div className="flex items-center justify-between bg-slate-800/40 rounded-lg p-2">
                          <p className="text-[9px] text-slate-400">Data Confidence</p>
                          <p className="text-[10px] font-bold text-amber-400">
                            {data.data_confidence_score}/100 — Preliminary Confidence
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Verification status — always show "Not Initiated" label unless truly VVB-verified */}
                  <div className="bg-slate-700/30 border border-slate-600/30 rounded-xl p-3 flex items-start gap-2"
                    data-testid="verification-status">
                    <Info size={12} className="text-slate-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-[9px] uppercase tracking-wide font-semibold mb-0.5 text-slate-400">
                        Verification Status
                      </p>
                      <p className="text-xs font-bold text-slate-300">
                        {verNotInitiated ? "Verification: Not Initiated" : verLabel}
                      </p>
                      <p className="text-[9px] text-slate-500 mt-0.5">
                        {verNotInitiated
                          ? "Independent VVB validation has not been initiated for this report."
                          : "Report reviewed — see project workspace for full verification details."}
                      </p>
                    </div>
                  </div>

                  {/* Disclaimer — always reinforces not independently verified for QR page */}
                  <div className="bg-slate-700/20 rounded-lg p-3 text-xs text-slate-400">
                    <p className="font-bold text-slate-300 mb-1">Disclaimer</p>
                    <p className="text-[10px] text-amber-400/90 font-semibold mb-1">
                      This report has been platform-reviewed but has not yet been independently validated by a VVB.
                    </p>
                    <p>{data.disclaimer || "This report is platform-generated and has NOT been validated by an accredited Validation/Verification Body (VVB). It is not a carbon credit or financial instrument."}</p>
                  </div>

                  {/* Report hash */}
                  <div className="border-t border-slate-700 pt-3">
                    <p className="text-[9px] text-slate-500 uppercase tracking-wide mb-1">Report Hash (SHA-256)</p>
                    <p className="text-[10px] font-mono text-slate-300 break-all">{data.report_hash}</p>
                  </div>
                </div>
              </div>
            );
          })()

        ) : data?._type === "fi" ? (
          /* ── FI Institutional Report Verification ─────────────────────── */
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden" data-testid="fi-verify-card">
            <div className={`border-b px-6 py-4 flex items-center gap-3 ${data.status === "superseded" ? "bg-amber-500/15 border-amber-500/20" : "bg-teal-500/15 border-teal-500/20"}`}>
              <CheckCircle2 size={20} className={data.status === "superseded" ? "text-amber-400" : "text-teal-400"} />
              <div>
                <p className={`text-sm font-bold ${data.status === "superseded" ? "text-amber-400" : "text-teal-400"}`}>
                  {data.status === "valid"
                    ? "Report Authenticated"
                    : data.status === "superseded"
                    ? "Superseded — Newer Version Available"
                    : "Invalid Hash"}
                </p>
                <p className="text-[10px] text-slate-500">DeCarbonIQ Institutional Finance Platform · Platform-generated</p>
              </div>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-[9px] text-slate-500 uppercase tracking-wide mb-0.5">Deal</p>
                <p className="text-lg font-bold text-white">{data.deal_name || data.deal_id}</p>
                <p className="text-xs text-slate-400">{data.report_type?.toUpperCase()} · Version {data.version}</p>
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                {[
                  ["Report ID",    data.report_id],
                  ["Framework",    data.framework?.split("(")[0]?.trim()],
                  ["Generated",    data.generated_at ? new Date(data.generated_at).toLocaleDateString() : "—"],
                  ["Completeness", data.completeness_pct ? `${data.completeness_pct.toFixed(0)}%` : "—"],
                ].map(([l, v]) => (
                  <div key={l} className="bg-slate-700/50 rounded-lg p-2.5">
                    <p className="text-[9px] text-slate-400 uppercase tracking-wide mb-0.5">{l}</p>
                    <p className="text-xs font-bold text-white truncate">{v || "—"}</p>
                  </div>
                ))}
              </div>
              {data.summary?.overall_status && (
                <div className={`rounded-lg p-3 text-xs ${data.summary.overall_status === "compliant" ? "bg-emerald-900/30 border border-emerald-500/20" : "bg-amber-900/30 border border-amber-500/20"}`}>
                  <p className={`font-bold uppercase text-[9px] tracking-wider mb-1 ${data.summary.overall_status === "compliant" ? "text-emerald-400" : "text-amber-400"}`}>Compliance Assessment</p>
                  <p className="text-white">{data.summary.overall_status?.toUpperCase()} — {data.summary.score_pct?.toFixed(0)}% criteria satisfied</p>
                </div>
              )}
              {data.status === "superseded" && (
                <div className="bg-amber-900/30 border border-amber-500/20 rounded-lg p-3 text-xs text-amber-300">
                  A newer version of this report has been generated. Hash: <span className="font-mono">{data.superseded_by?.slice(0, 16)}...</span>
                </div>
              )}
              <div className="border-t border-slate-700 pt-3">
                <p className="text-[9px] text-slate-500 mb-1 uppercase tracking-wide">Verification Hash (SHA-256)</p>
                <p className="font-mono text-[8.5px] text-slate-400 break-all">{data.hash}</p>
              </div>
            </div>
          </div>

        ) : (
          /* ── Legacy SME / Housing Report ─────────────────────────────── */
          <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden" data-testid="verify-card">
            <div className="bg-teal-500/15 border-b border-teal-500/20 px-6 py-4 flex items-center gap-3">
              <Shield size={20} className="text-teal-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-teal-400">Validation-Ready Model Output</p>
                <p className="text-[10px] text-slate-500">
                  {data?.project_type
                    ? `${data.methodology} | DeCarbonIQ`
                    : "DeCarbonIQ V1 Engine · GHG Protocol Corporate Standard"}
                </p>
              </div>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <p className="text-[9px] text-slate-500 uppercase tracking-wide mb-0.5">Organization</p>
                <p className="text-lg font-bold text-white">{data?.organization}</p>
                <p className="text-xs text-slate-400">{data?.sector} · {data?.country}</p>
              </div>

              <div className="bg-slate-900/60 rounded-xl px-4 py-3">
                {data?.project_type ? (
                  <>
                    <p className="text-[9px] text-slate-500 mb-1">Embodied Carbon Reduction</p>
                    <p className="text-2xl font-bold text-white">{data.embodied_reduction_tco2e} <span className="text-sm font-normal text-slate-400">tCO₂e/unit</span></p>
                    <p className="text-[10px] text-slate-500 mt-1">
                      Baseline: <span className="text-slate-300">{data.baseline_embodied_tco2e}t</span>
                      {" | "} Project: <span className="text-slate-300">{data.project_embodied_tco2e}t</span>
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-[9px] text-slate-500 mb-1">Total Annual Emissions</p>
                    <p className="text-2xl font-bold text-white">{data?.emissions_tco2e} <span className="text-sm font-normal text-slate-400">tCO₂e/yr</span></p>
                    {(data?.scope_1_tco2e > 0 || data?.scope_2_tco2e > 0) && (
                      <p className="text-[10px] text-slate-500 mt-1">
                        Scope 1: <span className="text-slate-300">{data?.scope_1_tco2e}t</span>
                        {" | "} Scope 2: <span className="text-slate-300">{data?.scope_2_tco2e}t</span>
                        {data?.scope_3_tco2e > 0 && <>{" | "} Scope 3: <span className="text-slate-300">{data?.scope_3_tco2e}t</span></>}
                      </p>
                    )}
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {(data?.project_type ? [
                  ["Operational Red.", `${data.operational_reduction_tco2e_yr} tCO₂e/yr`, "text-teal-400"],
                  ["3yr Cumulative",   `${data.cumulative_3yr_tco2e} tCO₂e/unit`, "text-white"],
                  ["Project Type",     data.project_type, "text-slate-300"],
                  ["Generated",        data.generated_at, "text-slate-300"],
                ] : [
                  ["CRS Score",     `${data?.crs_score}/100 (Band ${data?.crs_band})`, "text-teal-400"],
                  ["Data Confidence", data?.confidence, "text-slate-300"],
                  ["Reporting Year",  `FY${data?.reporting_year}`, "text-slate-300"],
                  ["Generated",       data?.generated_at, "text-slate-300"],
                ]).map(([l, v, c]) => (
                  <div key={l} className="bg-slate-900/60 rounded-xl px-3 py-2.5">
                    <p className="text-[9px] text-slate-500 mb-0.5">{l}</p>
                    <p className={`text-xs font-bold ${c}`}>{v}</p>
                  </div>
                ))}
              </div>

              {(data?.industry_benchmark) && (
                <div className="bg-slate-900/60 rounded-xl px-4 py-3">
                  <p className="text-[9px] text-slate-500 mb-1">Industry Benchmark</p>
                  {typeof data.industry_benchmark === "string" ? (
                    <p className="text-[10px] text-emerald-400 font-semibold">{data.industry_benchmark}</p>
                  ) : (
                    <>
                      <p className="text-xs text-slate-300">Range: <span className="font-semibold text-white">{data.industry_benchmark?.avg_range}</span></p>
                      <p className="text-[10px] text-emerald-400 font-semibold mt-0.5">{data.industry_benchmark?.vs_average}</p>
                    </>
                  )}
                </div>
              )}

              <div className="bg-slate-900/60 rounded-xl px-4 py-3">
                <p className="text-[9px] text-slate-500 mb-0.5">Calculation Basis</p>
                <p className="text-xs text-slate-300">{data?.methodology || "IPCC 2006 Guidelines"}</p>
              </div>

              <div className="pt-2 border-t border-slate-700 flex items-center justify-between">
                <div>
                  <p className="text-[9px] text-slate-500">Report ID</p>
                  <p className="text-[10px] font-mono text-slate-400">{data?.report_id}</p>
                </div>
                <Link to="/registry" className="text-[10px] text-teal-500 hover:underline flex items-center gap-1">
                  Registry <ExternalLink size={9} />
                </Link>
              </div>
            </div>

            <div className="px-6 pb-5">
              <p className="text-[9px] text-slate-600 leading-relaxed">{data?.disclaimer}</p>
            </div>
          </div>
        )}

        <p className="text-center mt-5 text-[9px] text-slate-600">
          DeCarbonIQ Climate Intelligence Platform ·{" "}
          <a href="https://decarboniq.com" className="text-teal-600 hover:underline">decarboniq.com</a>
        </p>
      </div>
    </div>
  );
}
